import { useEffect, useState } from 'preact/hooks';
import { getRecord, listAllRecordsWithLinks, type LinkRow, type RecordRow } from '../lib/api';
import { routePath } from '../routes/route';
import { openRecordEditor, openCreateMenu, useRecordsVersion } from '../lib/recordEditor';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import './ClientDetail.css';

// S4 — THE CLIENT SCREEN (wf02 §03, client()): "Fortinos is three sites,
// one billing address, one outstanding balance. The client screen leads
// with MONEY AND SITES, because that is what the office deals with at this
// level. Contacts are per-site, since the kitchen manager at Dundas is not
// the one at Plains."
//
// Money here is the honest computable analog of the prototype's
// "outstanding": the invoice_total_sum of this client's work orders
// sitting in awaiting_payment — the same definition the S1 home's money
// band uses. "Open" excludes done/cancelled/deposited.
//
// THE JOIN, AND ITS HONESTY LINE. Sites, work orders and equipment reached
// this screen through the record's OWN links (GET /records/:id?include=
// links), which the server pages at 50, oldest first, with no paging
// reachable from lib/api. Under that cap every number was exact; at the cap
// the recent joins were the ones missing, so a truncated read rendered '—'
// for money and a '+' on counts rather than an undercount presented as
// truth. "The real fix is a paged/bulk link read — named in the chunk
// report, not worked around with a lie," this header said.
//
// ---------------------------------------------------------------------
// S16 — THAT FIX ARRIVED, AND THE '—' IS GONE.
//
// S15 built the bulk link read. This screen no longer walks the client's
// own link page at all; it reads THE OTHER SIDE, one request per type,
// links included:
//
//   the sites      hvac_site       + [client]
//   the orders     hvac_work_order + [wo_client, wo_site]
//   the equipment  hvac_equipment  + [equipment_client, site]
//   the client     GET /records/:id (its name; nothing hangs off it)
//
// FOUR reads (five for a book past 200 work orders) where a fifty-site
// chain cost twenty-five, and every number is now EXACT — because a site's
// membership of this client is read off the site, an order's off the order,
// a machine's off the machine, and none of those is a page that can run
// out. Two caps go with it: SITES_SHOWN_CAP (24) rationed how many sites
// the screen would even resolve, and LINK_PAGE (50) was this file mirroring
// a server constant in order to GUESS at truncation. A guess is wrong in
// both directions; the server reports `links_truncated` as a fact, and a
// per-record set of two or three named relationships can no longer reach
// it anyway.
//
// WHAT STAYS IS THE HONESTY, NOT THE CAP. If the server ever does refuse or
// cut short one of these reads, the ids it names are excluded from every
// count and the screen says which — an unread site is not a site with no
// work, and this screen will not add it up as though it were.

export interface ClientDetailProps {
  tenantId: string;
  id: string;
  onNavigate: (path: string) => void;
}

type LoadState = 'loading' | 'ready' | 'error';

const SITE_TYPE = 'hvac_site';
const WO_TYPE = 'hvac_work_order';
const EQUIPMENT_TYPE = 'hvac_equipment';

/** The relationships each read actually renders, named in the request so
 *  the server sends those and nothing else. `client` is hvac_site's own
 *  master_detail parent — the link that says which chain a location belongs
 *  to. */
const SITE_LINK_TYPES = ['client'];
const WO_LINK_TYPES = ['wo_client', 'wo_site'];
const EQUIPMENT_LINK_TYPES = ['equipment_client', 'site'];

const CLOSED_STATES = new Set(['done', 'cancelled', 'deposited']);

interface SiteView {
  row: RecordRow;
  woIds: string[];
  equipmentIds: string[];
}

function money(n: number): string {
  return n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function woTotal(r: RecordRow): number {
  const v = Number(r.fields['invoice_total_sum'] ?? 0);
  return Number.isFinite(v) ? v : 0;
}

function isOpen(r: RecordRow): boolean {
  return !CLOSED_STATES.has(r.lifecycle_state ?? '');
}

function textField(r: RecordRow, name: string): string {
  const v = r.fields[name];
  return typeof v === 'string' ? v : '';
}

/** The other end of a relationship, whichever side of the link row this
 *  record sits on (the same helper the spine and the board use). */
function linkedId(links: LinkRow[], selfId: string, linkType: string): string | null {
  for (const l of links) {
    if (l.link_type !== linkType) continue;
    if (l.src_id === selfId) return l.dst_id;
    if (l.dst_id === selfId) return l.src_id;
  }
  return null;
}

/** How many rows of each kind the server would not answer links for. Every
 *  count on this screen is exact when all three are zero — which is the
 *  normal case, and which is why the screen can finally show a total
 *  instead of a dash. */
interface Unread {
  sites: number;
  orders: number;
  equipment: number;
}

export function ClientDetail({ tenantId, id, onNavigate }: ClientDetailProps) {
  const [load, setLoad] = useState<LoadState>('loading');
  // S16: the FAILURE, in the product's own words — a refused read never
  // renders as a client with no sites and no money.
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [client, setClient] = useState<RecordRow | null>(null);
  const [sites, setSites] = useState<SiteView[]>([]);
  const [clientWos, setClientWos] = useState<RecordRow[]>([]);
  const [equipmentCount, setEquipmentCount] = useState(0);
  const [unread, setUnread] = useState<Unread>({ sites: 0, orders: 0, equipment: 0 });
  const [woById, setWoById] = useState<Map<string, RecordRow>>(new Map());
  const [retryNonce, setRetryNonce] = useState(0);
  const recordsVersion = useRecordsVersion();

  useEffect(() => {
    let cancelled = false;
    async function loadAll() {
      setLoad('loading');
      setFailure(null);
      try {
        // FOUR reads. Membership is read off the CHILD in every case — a
        // site says which client it belongs to, an order says which site and
        // client, a machine says which site — so no count here depends on a
        // page of the client's own links that could run out.
        const [record, siteLinks, orderLinks, equipmentLinks] = await Promise.all([
          getRecord(tenantId, id),
          listAllRecordsWithLinks(tenantId, SITE_TYPE, { linkTypes: SITE_LINK_TYPES }),
          listAllRecordsWithLinks(tenantId, WO_TYPE, { linkTypes: WO_LINK_TYPES }),
          listAllRecordsWithLinks(tenantId, EQUIPMENT_TYPE, { linkTypes: EQUIPMENT_LINK_TYPES }),
        ]);
        if (cancelled) return;

        // An id the server would not answer for, or had to cut short, is one
        // whose membership cannot be decided: a missing `client` link in a
        // truncated set is not evidence the site belongs to somebody else.
        // Those rows are left OUT of every total and counted, so the screen
        // can say the total is short and by how much.
        const unansweredIn = (batch: { unreadable: string[]; truncated: string[] }) =>
          new Set([...batch.unreadable, ...batch.truncated]);
        const siteUnanswered = unansweredIn(siteLinks);
        const orderUnanswered = unansweredIn(orderLinks);
        const equipmentUnanswered = unansweredIn(equipmentLinks);

        const mySites = siteLinks.records.filter(
          (s) =>
            !siteUnanswered.has(s.id) &&
            linkedId(siteLinks.linksByRecordId[s.id] ?? [], s.id, 'client') === id
        );
        const mySiteIds = new Set(mySites.map((s) => s.id));

        const woSite = new Map<string, string | null>();
        const myOrders: RecordRow[] = [];
        for (const w of orderLinks.records) {
          if (orderUnanswered.has(w.id)) continue;
          const links = orderLinks.linksByRecordId[w.id];
          if (links === undefined) continue;
          const siteId = linkedId(links, w.id, 'wo_site');
          const clientId = linkedId(links, w.id, 'wo_client');
          if (clientId !== id && !(siteId !== null && mySiteIds.has(siteId))) continue;
          woSite.set(w.id, siteId);
          myOrders.push(w);
        }

        const equipmentSite = new Map<string, string | null>();
        let myEquipment = 0;
        for (const e of equipmentLinks.records) {
          if (equipmentUnanswered.has(e.id)) continue;
          const links = equipmentLinks.linksByRecordId[e.id];
          if (links === undefined) continue;
          const siteId = linkedId(links, e.id, 'site');
          const clientId = linkedId(links, e.id, 'equipment_client');
          if (clientId !== id && !(siteId !== null && mySiteIds.has(siteId))) continue;
          equipmentSite.set(e.id, siteId);
          myEquipment += 1;
        }

        setClient(record);
        setSites(
          mySites.map((row) => ({
            row,
            woIds: myOrders.filter((w) => woSite.get(w.id) === row.id).map((w) => w.id),
            equipmentIds: [...equipmentSite.entries()]
              .filter(([, siteId]) => siteId === row.id)
              .map(([eid]) => eid),
          }))
        );
        setClientWos(myOrders);
        setEquipmentCount(myEquipment);
        setWoById(new Map(orderLinks.records.map((w) => [w.id, w])));
        setUnread({
          sites: siteLinks.records.filter((s) => siteUnanswered.has(s.id)).length,
          orders: orderLinks.records.filter((w) => orderUnanswered.has(w.id)).length,
          equipment: equipmentLinks.records.filter((e) => equipmentUnanswered.has(e.id)).length,
        });
        setLoad('ready');
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setLoad('error');
      }
    }
    void loadAll();
    return () => {
      cancelled = true;
    };
  }, [tenantId, id, recordsVersion, retryNonce]);

  const crumb = (current: string) => (
    <nav class="client-detail__crumb" aria-label="Breadcrumb">
      <button type="button" onClick={() => onNavigate(routePath({ screen: 'today' }))}>
        Ether Inc
      </button>
      <span class="client-detail__crumb-sep" aria-hidden="true">
        ›
      </span>
      <button type="button" onClick={() => onNavigate(routePath({ screen: 'clients' }))}>
        Clients
      </button>
      <span class="client-detail__crumb-sep" aria-hidden="true">
        ›
      </span>
      <span class="client-detail__crumb-cur">{current}</span>
    </nav>
  );

  if (load === 'loading') {
    return (
      <div class="client-detail">
        {crumb('…')}
        <div class="client-detail__say">One moment…</div>
      </div>
    );
  }

  if (load === 'error' && failure !== null) {
    // NOT "no sites, nothing outstanding". A refused read says which refusal
    // it was, and a temporary one offers the door out.
    return (
      <div class="client-detail">
        {crumb('…')}
        <div class="client-detail__say">
          {failure.headline}
          <br />
          <span class="client-detail__say-soft">{failure.detail}</span>
        </div>
        {failure.retryable ? (
          <button
            type="button"
            class="client-detail__retry"
            onClick={() => setRetryNonce((n) => n + 1)}
          >
            Read it again
          </button>
        ) : null}
      </div>
    );
  }

  if (!client) return null;

  // S16: not "the array looked 50 long", but "the server named these rows
  // as ones it could not answer for". Normally all three are zero and every
  // number below is exact.
  const unreadTotal = unread.sites + unread.orders + unread.equipment;
  const anyUnread = unreadTotal > 0;

  const siteTotal = sites.length;
  const openWos = clientWos.filter(isOpen);
  const awaiting = clientWos.filter((w) => w.lifecycle_state === 'awaiting_payment');
  const outstanding = awaiting.reduce((sum, w) => sum + woTotal(w), 0);

  const closedCount = clientWos.length - openWos.length;
  const plus = anyUnread ? '+' : '';

  function siteAggregates(s: SiteView): { outstanding: number; open: number; urgent: boolean } {
    const rows = s.woIds.map((wid) => woById.get(wid)).filter((w): w is RecordRow => w !== undefined);
    return {
      outstanding: rows
        .filter((w) => w.lifecycle_state === 'awaiting_payment')
        .reduce((sum, w) => sum + woTotal(w), 0),
      open: rows.filter(isOpen).length,
      urgent: rows.some((w) => w.fields['urgent'] === true && isOpen(w)),
    };
  }

  const billing = textField(client, 'billing_address');
  const billingName = textField(client, 'billing_name');
  const contactName = textField(client, 'principal_contact_name');
  const contactPhone = textField(client, 'principal_contact_phone');
  const contactEmail = textField(client, 'principal_contact_email');

  // Flagged: the honest analogs of the prototype's own flags — urgent open
  // work, and work sitting in awaiting_payment. S7: the work order the flag
  // was derived FROM rides on the flag (it was always known here and simply
  // never carried), with its site where one is linked — so each flag renders
  // as a doorway straight to that work order, no site hunt.
  const siteByWoId = new Map<string, RecordRow>();
  for (const s of sites) {
    for (const wid of s.woIds) if (!siteByWoId.has(wid)) siteByWoId.set(wid, s.row);
  }
  interface FlagRow {
    severity: string;
    text: string;
    woId?: string;
    siteId?: string;
    siteName?: string;
  }
  const flagFor = (w: RecordRow, severity: string, text: string): FlagRow => {
    const site = siteByWoId.get(w.id);
    return { severity, text, woId: w.id, siteId: site?.id, siteName: site?.display_name };
  };
  const flags: FlagRow[] = [
    ...clientWos
      .filter((w) => w.fields['urgent'] === true && isOpen(w))
      .map((w) => flagFor(w, 'var(--status-critical)', `${w.display_name} — urgent`)),
    ...awaiting.map((w) =>
      flagFor(w, 'var(--status-warning)', `${w.display_name} — $${money(woTotal(w))} awaiting payment`)
    ),
  ];

  return (
    <div class="client-detail">
      {crumb(client.display_name)}

      <div class="client-detail__hero">
        <div>
          <div class="client-detail__eyebrow">
            Client · {siteTotal === 1 ? '1 site' : `${siteTotal} sites`}
          </div>
          <h2 class="client-detail__title">{client.display_name}</h2>
          {billing || billingName ? (
            <div class="client-detail__sub">{[billingName, billing].filter(Boolean).join(' · ')}</div>
          ) : null}
        </div>
        <button
          type="button"
          class="client-detail__edit"
          onClick={() => openRecordEditor({ mode: 'edit', typeKey: 'hvac_client', id })}
        >
          Edit
        </button>
      </div>

      <div class="client-detail__cols">
        <div>
          <div class="client-detail__sec">
            <span>Sites</span>
            <span class="client-detail__mini">{siteTotal}</span>
          </div>
          {sites.length === 0 ? (
            <div class="client-detail__empty">No sites yet.</div>
          ) : (
            sites.map((s) => {
              const agg = siteAggregates(s);
              return (
                <button
                  type="button"
                  class="client-detail__row"
                  key={s.row.id}
                  onClick={() => onNavigate(routePath({ screen: 'siteDetail', id: s.row.id }))}
                >
                  <span
                    class="client-detail__stripe"
                    style={{
                      background: agg.urgent
                        ? 'var(--status-critical)'
                        : agg.outstanding > 0
                          ? 'var(--status-warning)'
                          : 'transparent',
                    }}
                  />
                  <span class="client-detail__row-main">
                    <b>{s.row.display_name}</b>
                    {textField(s.row, 'service_address') ? (
                      <small>{textField(s.row, 'service_address')}</small>
                    ) : null}
                  </span>
                  <span class="client-detail__row-right">
                    {agg.outstanding > 0 ? `$${money(agg.outstanding)}` : '—'}
                    <span class="client-detail__row-st">{`${agg.open}${plus} open`}</span>
                  </span>
                </button>
              );
            })
          )}
          {anyUnread ? (
            // S16 replaces "Showing the first 24 of 51" — a cap this screen
            // chose — with the only thing that can shorten a list now: rows
            // the server would not answer for. Named, counted, and never
            // silently folded into the totals beside them.
            <div class="client-detail__soft">
              {unreadTotal === 1 ? '1 record' : `${unreadTotal} records`} could not be read, so the
              numbers here are at least what they say.
            </div>
          ) : null}

          <div class="client-detail__sec client-detail__sec--later">
            <span>Account contacts</span>
            <span class="client-detail__mini">chain level</span>
          </div>
          {contactName || contactPhone || contactEmail ? (
            <>
              {contactName ? (
                <div class="client-detail__kv">
                  <span>Contact</span>
                  <b class="client-detail__kv-soft">{contactName}</b>
                </div>
              ) : null}
              {contactPhone ? (
                <div class="client-detail__kv">
                  <span>Phone</span>
                  <b class="client-detail__kv-soft">{contactPhone}</b>
                </div>
              ) : null}
              {contactEmail ? (
                <div class="client-detail__kv">
                  <span>Email</span>
                  <b class="client-detail__kv-soft">{contactEmail}</b>
                </div>
              ) : null}
            </>
          ) : (
            <div class="client-detail__empty">No account contact recorded yet.</div>
          )}

          <div class="client-detail__sec client-detail__sec--later">
            <span>Flagged</span>
          </div>
          {flags.length === 0 ? (
            <div class="client-detail__land">
              <span class="client-detail__dot" style={{ background: 'var(--status-good)' }} />
              <span>Nothing flagged</span>
            </div>
          ) : (
            // S7: a flag with a known work order is a doorway to it, in the
            // same row idiom as the sites above (stripe · main). The site
            // rides along as the small line, so which location the flag
            // belongs to is visible without hunting. A flag with no
            // resolvable subject stays plain text — never a dead handle.
            flags.map((f) => {
              const woId = f.woId;
              return woId !== undefined ? (
                <button
                  type="button"
                  class="client-detail__row"
                  key={`${woId}-${f.text}`}
                  onClick={() => onNavigate(routePath({ screen: 'workOrderDetail', id: woId }))}
                >
                  <span class="client-detail__stripe" style={{ background: f.severity }} />
                  <span class="client-detail__row-main">
                    <b>{f.text}</b>
                    {f.siteName !== undefined ? <small>{f.siteName}</small> : null}
                  </span>
                </button>
              ) : (
                <div class="client-detail__land" key={f.text}>
                  <span class="client-detail__dot" style={{ background: f.severity }} />
                  <span>{f.text}</span>
                </div>
              );
            })
          )}
        </div>

        <div>
          <div class="client-detail__sec">
            <span>Outstanding</span>
          </div>
          <div
            class="client-detail__big"
            style={{ color: outstanding > 0 ? 'var(--status-critical)' : 'var(--ink)' }}
          >
            {anyUnread ? '—' : `$${money(outstanding)}`}
          </div>
          {anyUnread ? (
            // The line S15's spec called out: a total the screen could not
            // know rendered as a dash. It still does — but only when the
            // server actually withheld rows, and it now says which, rather
            // than every chain over fifty links being told it has "more
            // history than one read can count".
            <div class="client-detail__soft">
              {unreadTotal === 1 ? 'One record' : `${unreadTotal} records`} went unread, so this
              would be an undercount.
            </div>
          ) : null}
          <div class="client-detail__kv">
            <span>Open work orders</span>
            <b>
              {openWos.length}
              {plus}
            </b>
          </div>
          {billing ? (
            <div class="client-detail__kv">
              <span>Billing address</span>
              <b class="client-detail__kv-soft client-detail__kv-right">{billing}</b>
            </div>
          ) : null}
          {contactEmail ? (
            <div class="client-detail__kv">
              <span>Email</span>
              <b class="client-detail__kv-soft">{contactEmail}</b>
            </div>
          ) : null}

          <div class="client-detail__sec client-detail__sec--later">
            <span>Actions</span>
          </div>
          <div class="client-detail__pills">
            <button
              type="button"
              class="client-detail__pill"
              onClick={() =>
                openRecordEditor({
                  mode: 'create',
                  typeKey: 'hvac_work_order',
                  prefillRels: { wo_client: [id] },
                })
              }
            >
              New work order
            </button>
            <button
              type="button"
              class="client-detail__pill"
              onClick={() =>
                openRecordEditor({ mode: 'create', typeKey: 'hvac_site', prefillRels: { client: [id] } })
              }
            >
              Add site
            </button>
          </div>
        </div>
      </div>

      <div class="client-detail__rail">
        <span class="client-detail__rail-label">Across this client</span>
        <span class="client-detail__pill client-detail__pill--fact client-detail__pill--on">
          <b>{siteTotal}</b> Sites
        </span>
        <span class="client-detail__pill client-detail__pill--fact">
          <b>
            {openWos.length}
            {plus}
          </b>{' '}
          Open WOs
        </span>
        <span class="client-detail__pill client-detail__pill--fact">
          <b>
            {closedCount}
            {plus}
          </b>{' '}
          Closed WOs
        </span>
        <span class="client-detail__pill client-detail__pill--fact">
          <b>
            {equipmentCount}
            {plus}
          </b>{' '}
          Equipment
        </span>
        <button type="button" class="client-detail__pill client-detail__pill--add" onClick={openCreateMenu}>
          + Add
        </button>
      </div>
    </div>
  );
}
